import type { ReactNode } from 'react'
import styles from './DataTable.module.css'

export type Align = 'left' | 'center' | 'right'

export interface Column<Row> {
  key: string
  header?: ReactNode
  align?: Align
  width?: number | string
  render: (row: Row, index: number) => ReactNode
}

export interface DataTableProps<Row> {
  columns: Column<Row>[]
  rows: Row[]
  rowKey: (row: Row, index: number) => string | number
  /* Tighter cells for dense forms (preset levels, wheel options). */
  compact?: boolean
  /* Chart density: one-letter cells, tiny headers (strategy grids). */
  dense?: boolean
  /* Hairline between rows. */
  bordered?: boolean
  /* Caps height and scrolls the body (strategy lists). */
  maxHeight?: number | string
  rowTone?: (row: Row, index: number) => 'default' | 'highlight' | 'muted'
  /* Accessible table name. */
  'aria-label'?: string
}

export function DataTable<Row>({
  columns, rows, rowKey, compact, dense, bordered = true, maxHeight, rowTone, ...rest
}: DataTableProps<Row>) {
  const alignClass = (a?: Align) => a === 'center' ? styles.center : a === 'right' ? styles.right : undefined
  const hasHeader = columns.some((c) => c.header)
  return (
    <div className={[styles.wrap, maxHeight !== undefined && styles.scroll].filter(Boolean).join(' ')} style={maxHeight !== undefined ? { maxHeight } : undefined}>
      <table className={[styles.table, compact && styles.compact, dense && styles.dense, bordered && styles.bordered].filter(Boolean).join(' ')} {...rest}>
        {hasHeader && (
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={[styles.th, alignClass(c.align)].filter(Boolean).join(' ')} style={c.width !== undefined ? { width: c.width } : undefined}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, i) => {
            const tone = rowTone?.(row, i) ?? 'default'
            return (
              <tr key={rowKey(row, i)} className={tone === 'highlight' ? styles.rowHighlight : tone === 'muted' ? styles.rowMuted : undefined}>
                {columns.map((c) => (
                  <td key={c.key} className={[styles.td, alignClass(c.align)].filter(Boolean).join(' ')}>{c.render(row, i)}</td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
